import { BeatUrlRefusal } from './beatUrl'

/**
 * Map a PostgREST error from an import_beats write to a form field and a translation
 * message key (D-14), so a raw DB code is never what Dominique reads.
 *
 * PostgREST errors carry code / message / details / hint and no constraint
 * field, so a CHECK refusal (23514) is told apart by the constraint name inside
 * the message. The constraints are the ones in
 * mobile/supabase/migrations/20240101000027_import_beats.sql and
 * mobile/supabase/migrations/20240101000028_newsroom_intake_and_beat_area.sql;
 * the municipality trigger raises 23514 with its name in the message on purpose.
 *
 * Pure: no Supabase call and no translation lookup here. Plan 30-07 adds the translations of
 * every key in BEAT_MESSAGE_KEYS.
 */

export const BEAT_MESSAGE_KEYS = [
  'beats.refusal.empty',
  'beats.refusal.numeric_profile',
  'beats.refusal.not_https',
  'beats.refusal.not_facebook',
  'beats.refusal.not_single_segment',
  'beats.refusal.reserved',
  'beats.refusal.bad_handle',
  'beats.error.duplicate',
  'beats.error.area_required',
  'beats.error.area_unknown',
  'beats.error.area_gone',
  'beats.error.not_admin',
  'beats.error.generic'
] as const

export type BeatMessageKey = (typeof BEAT_MESSAGE_KEYS)[number]

export type BeatErrorField = 'page_url' | 'area' | 'general'

export type BeatErrorMessage = { field: BeatErrorField; messageKey: BeatMessageKey }

/** The message key for a deriveBeat refusal. */
export const refusalMessageKey = (reason: BeatUrlRefusal): BeatMessageKey =>
  `beats.refusal.${reason}`

const GENERIC: BeatErrorMessage = { field: 'general', messageKey: 'beats.error.generic' }

/** 23514 constraint name → field + key. Order does not matter: the names are disjoint. */
const CHECK_CONSTRAINTS: ReadonlyArray<[string, BeatErrorMessage]> = [
  ['import_beats_page_url_single_segment', { field: 'page_url', messageKey: 'beats.refusal.not_single_segment' }],
  ['import_beats_page_url_facebook', { field: 'page_url', messageKey: 'beats.refusal.not_facebook' }],
  ['import_beats_handle_shape', { field: 'page_url', messageKey: 'beats.refusal.bad_handle' }],
  ['import_beats_handle_matches_url', { field: 'page_url', messageKey: 'beats.refusal.bad_handle' }],
  ['import_beats_area_exactly_one', { field: 'area', messageKey: 'beats.error.area_required' }],
  ['import_beats_area_municipality_known', { field: 'area', messageKey: 'beats.error.area_unknown' }]
]

const readString = (source: Record<string, unknown>, key: string): string => {
  const value = source[key]
  return typeof value === 'string' ? value : ''
}

export const mapBeatError = (error: unknown): BeatErrorMessage => {
  if (typeof error !== 'object' || error === null) return GENERIC

  const source = error as Record<string, unknown>
  const code = readString(source, 'code')
  const text = `${readString(source, 'message')} ${readString(source, 'details')}`

  if (code === '23505') return { field: 'page_url', messageKey: 'beats.error.duplicate' }

  if (code === '23514') {
    const hit = CHECK_CONSTRAINTS.find(([name]) => text.includes(name))
    return hit ? hit[1] : GENERIC
  }

  if (code === '23503') return { field: 'area', messageKey: 'beats.error.area_gone' }
  if (code === '42501') return { field: 'general', messageKey: 'beats.error.not_admin' }

  return GENERIC
}
